import { Box, Typography } from "@mui/material";
import { LayoutWrapper } from "../../../ReusableComponents/LayoutWrapper";
import { TaxonomyPath } from "../../../ReusableComponents/TaxonomyPath";
import { SPACING } from "../../../../utils/constants/designConstants";
import { SpendMetricsSection } from "./SpendMetricsSections";
import { DemandMetricsSection } from "./DemandMetrics";
import { SpendTable } from "./SpendTable";
import {
  SUPPLIER_COLUMNS,
  DEPARTMENT_COLUMNS,
  PROCUREMENT_LEVERS_COLUMNS,
  BUSINESS_UNIT_COLUMNS,
  BAR_PADDING_X,
  BAR_PADDING_Y,
  BAR_TABLE_GAP,
} from "./constants";
import { GeneralInsights } from "./GeneralInsights";
import { TitleBar } from "./utils/SpendFunctions";

export const SpendSection = ({
  taxonomyPath,
  spendOverview,
  supplierRows,
  departmentRows,
  businessUnitRows,
  insights,
  procurementLevers,
}) => {
  // Procurement lever transform data
  const procurementLeversRows = procurementLevers
    ? Object.entries(procurementLevers).map(([name, value]) => ({
        lever: name.charAt(0).toUpperCase() + name.slice(1),
        strength: `${Math.round(value * 100)}%`,
      }))
    : [];

  return (
    <LayoutWrapper>
      <Box
        id="spend"
        component="section"
        aria-label="spend"
        className="generate-insights-content-inside-box"
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ mb: SPACING.HEADING_BOTTOM, mt: SPACING.HEADING_TOP }}>
          <Typography variant="h1" component="h1">
            Spend
          </Typography>

          {/* Show taxonomy path */}
          <TaxonomyPath taxonomyPath={taxonomyPath} />
        </Box>

        <Box
          sx={{
            gap: SPACING.SECTION_GAP,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Spend Metrics */}
          <SpendMetricsSection spendOverview={spendOverview} />

          {/* Spend By Top Suppliers */}
          <Box>
            <TitleBar title={"Spend By Top Suppliers"} />
            {supplierRows?.length ? (
              <SpendTable
                columns={SUPPLIER_COLUMNS}
                rows={supplierRows.slice(0, 10)}
              />
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                No spend data available for top suppliers.
              </Typography>
            )}
          </Box>

          {/* Spend By Department */}
          <Box>
            <TitleBar title={"Spend By Department"} />
            {departmentRows?.length ? (
              <SpendTable columns={DEPARTMENT_COLUMNS} rows={departmentRows} />
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                No spend data available for locations.
              </Typography>
            )}
          </Box>

          {/* Spend By Business Unit */}
          <Box>
            <TitleBar title={"Spend By Business Unit"} />
            {businessUnitRows?.length ? (
              <SpendTable
                columns={BUSINESS_UNIT_COLUMNS}
                rows={businessUnitRows}
              />
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                No spend data available for business units.
              </Typography>
            )}
          </Box>

          {/* General L3 and Strategy Savings Insights */}
          <Box>
            <GeneralInsights insights={insights} />
          </Box>

          {/* Procurement Levers */}
          <Box>
            <TitleBar title={"Procurement Levers"} />
            {procurementLeversRows?.length ? (
              <SpendTable
                columns={PROCUREMENT_LEVERS_COLUMNS}
                rows={procurementLeversRows}
              />
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                No spend data available for procurement levers.
              </Typography>
            )}
          </Box>

          {/* Demand Metrics */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: BAR_TABLE_GAP,
                px: BAR_PADDING_X,
                py: BAR_PADDING_Y,
              }}
            >
              Demand Metrics
            </Typography>
            {procurementLeversRows?.length ? (
              <DemandMetricsSection keyMetrics={insights?.key_metrics} />
            ) : (
              <Typography sx={{ color: "text.secondary" }}>
                No spend data available for demand metrics.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </LayoutWrapper>
  );
};
